import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

const POST_COLUMNS =
  "id, slug, title, excerpt, body_markdown, cover_image_url, author_name, tags, seo_title, seo_description, status, scheduled_for, published_at, created_at, updated_at, key_takeaways, faqs, lead_magnet_key";

const TESTIMONIAL_TAG = "testimonials";

export const listPublishedPosts = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("blog_posts")
    .select(POST_COLUMNS)
    .eq("status", "published")
    .lte("published_at", new Date().toISOString())
    .order("published_at", { ascending: false })
    .limit(100);
  if (error) throw new Error(error.message);
  // Testimonial stories live on /testimonials, not in the MOTM Insights feed.
  return (data ?? []).filter(
    (p: any) => !(p.tags ?? []).some((t: string) => t.toLowerCase() === TESTIMONIAL_TAG),
  );
});

export const listTestimonialPosts = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("blog_posts")
    .select("slug, title, excerpt, cover_image_url, published_at, author_name, tags")
    .eq("status", "published")
    .lte("published_at", new Date().toISOString())
    .contains("tags", [TESTIMONIAL_TAG])
    .order("published_at", { ascending: false })
    .limit(100);
  if (error) throw new Error(error.message);
  return data ?? [];
});


export const getPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: post, error } = await supabase
      .from("blog_posts")
      .select(POST_COLUMNS)
      .eq("slug", data.slug)
      .eq("status", "published")
      .lte("published_at", new Date().toISOString())
      .maybeSingle();
    if (error) throw new Error(error.message);
    return post;
  });

export const listRelatedPosts = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z.object({
      slug: z.string().min(1),
      tags: z.array(z.string()).default([]),
      limit: z.number().int().min(1).max(6).default(3),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const base = supabase
      .from("blog_posts")
      .select("slug, title, excerpt, cover_image_url, published_at, tags")
      .eq("status", "published")
      .lte("published_at", new Date().toISOString())
      .neq("slug", data.slug)
      .order("published_at", { ascending: false })
      .limit(data.limit);
    const q = data.tags.length ? base.overlaps("tags", data.tags) : base;
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    if (rows && rows.length >= data.limit) return rows;
    // Fallback: fill with most recent regardless of tags
    const { data: extra } = await supabase
      .from("blog_posts")
      .select("slug, title, excerpt, cover_image_url, published_at, tags")
      .eq("status", "published")
      .lte("published_at", new Date().toISOString())
      .neq("slug", data.slug)
      .order("published_at", { ascending: false })
      .limit(data.limit + 5);
    const seen = new Set((rows ?? []).map((r) => r.slug));
    const merged = [...(rows ?? [])];
    for (const r of extra ?? []) {
      if (merged.length >= data.limit) break;
      if (!seen.has(r.slug)) merged.push(r);
    }
    return merged;
  });



async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .eq("role", "admin")
      .maybeSingle();
    return { isAdmin: Boolean(data) };
  });

// Bootstrap: claim admin if no admin exists yet (safe one-shot self-grant)
export const claimAdminIfFirst = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { count, error: cErr } = await supabaseAdmin
      .from("user_roles")
      .select("*", { count: "exact", head: true })
      .eq("role", "admin");
    if (cErr) throw new Error(cErr.message);
    if ((count ?? 0) > 0) return { granted: false };
    const { error } = await supabaseAdmin
      .from("user_roles")
      .insert({ user_id: context.userId, role: "admin" });
    if (error) throw new Error(error.message);
    return { granted: true };
  });

export const listAllPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("blog_posts")
      .select(POST_COLUMNS)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getPostById = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { data: post, error } = await context.supabase
      .from("blog_posts")
      .select(POST_COLUMNS)
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return post;
  });

const postInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/, "lowercase letters, numbers, hyphens"),
  title: z.string().min(1),
  excerpt: z.string().optional().nullable(),
  body_markdown: z.string().default(""),
  cover_image_url: z.string().url().optional().nullable().or(z.literal("")),
  author_name: z.string().default("Kristan Cole Network"),
  tags: z.array(z.string()).default([]),
  seo_title: z.string().optional().nullable(),
  seo_description: z.string().optional().nullable(),
  status: z.enum(["draft", "scheduled", "published"]).default("draft"),
  scheduled_for: z.string().datetime().optional().nullable(),
  published_at: z.string().datetime().optional().nullable(),
  key_takeaways: z.array(z.string()).default([]),
  faqs: z.array(z.object({ q: z.string().min(1), a: z.string().min(1) })).default([]),
  lead_magnet_key: z.string().max(80).optional().nullable(),
});

export const upsertPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => postInput.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const payload: any = { ...data };
    if (payload.cover_image_url === "") payload.cover_image_url = null;
    if (payload.status === "published" && !payload.published_at) {
      payload.published_at = new Date().toISOString();
    }
    if (payload.status === "scheduled" && !payload.scheduled_for) {
      throw new Error("Scheduled posts need a scheduled_for date");
    }
    if (!payload.id) payload.created_by = context.userId;
    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .upsert(payload, { onConflict: "id" })
      .select(POST_COLUMNS)
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

export const deletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("blog_posts").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const importRow = z.object({
  slug: z.string().min(1),
  title: z.string().min(1),
  excerpt: z.string().optional().nullable(),
  body_markdown: z.string().default(""),
  cover_image_url: z.string().optional().nullable(),
  author_name: z.string().optional().nullable(),
  tags: z.string().optional().nullable(),
  seo_title: z.string().optional().nullable(),
  seo_description: z.string().optional().nullable(),
  scheduled_for: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
});

export const bulkImportPosts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ rows: z.array(importRow).min(1).max(500) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const now = new Date();
    const records = data.rows.map((r) => {
      const slug = r.slug.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-");
      const scheduled = r.scheduled_for ? new Date(r.scheduled_for) : null;
      let status: "draft" | "scheduled" | "published" = "draft";
      let published_at: string | null = null;
      if (r.status && ["draft", "scheduled", "published"].includes(r.status)) {
        status = r.status as any;
      } else if (scheduled) {
        status = scheduled <= now ? "published" : "scheduled";
      }
      if (status === "published") published_at = (scheduled ?? now).toISOString();
      const tags = r.tags
        ? r.tags.split(/[|,]/).map((t) => t.trim()).filter(Boolean)
        : [];
      return {
        slug,
        title: r.title,
        excerpt: r.excerpt || null,
        body_markdown: r.body_markdown || "",
        cover_image_url: r.cover_image_url || null,
        author_name: r.author_name || "Kristan Cole Network",
        tags,
        seo_title: r.seo_title || null,
        seo_description: r.seo_description || null,
        status,
        scheduled_for: scheduled ? scheduled.toISOString() : null,
        published_at,
        created_by: context.userId,
      };
    });
    const { data: rows, error } = await context.supabase
      .from("blog_posts")
      .upsert(records, { onConflict: "slug" })
      .select("id, slug, status");
    if (error) throw new Error(error.message);
    return { inserted: rows?.length ?? 0, rows };
  });
